import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Loader2, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import ThemeToggle from '../components/ThemeToggle';
import { useBrandSettings } from '../context/BrandSettingsContext';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const UpgradeSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logos } = useBrandSettings();
  const [checking, setChecking] = useState(true);
  const [upgradeStatus, setUpgradeStatus] = useState(null);
  
  // Check if user is logged in and if they have a pending upgrade
  useEffect(() => {
    const checkUpgradeStatus = async () => {
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          // Check for pending upgrade
          const response = await apiService.request(`/upgrade/check-pending?email=${encodeURIComponent(user.email)}`);
          
          if (response.applied) {
            // Upgrade was applied - update local storage
            user.subscription_tier = 'pro';
            localStorage.setItem('user', JSON.stringify(user));
            setUpgradeStatus('upgraded');
          } else if (user.subscription_tier === 'pro') {
            setUpgradeStatus('already_pro');
          } else {
            setUpgradeStatus('pending');
          }
        } catch (error) {
          console.error('Error checking upgrade:', error);
          setUpgradeStatus('pending');
        }
      } else {
        setUpgradeStatus('not_logged_in');
      }
      
      setChecking(false);
    };
    
    checkUpgradeStatus();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isUpgraded = upgradeStatus === 'upgraded' || upgradeStatus === 'already_pro';
  const needsLogin = upgradeStatus === 'not_logged_in' || upgradeStatus === 'pending';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            {logos?.default ? (
              <img src={logos.default} alt="KaiCheckIt" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
            ) : (
              <BrandLogo size="responsive" data-testid="brand-logo-header" />
            )}
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-lg mx-auto">
          <Card className="border-2 border-green-200 dark:border-green-800 bg-white dark:bg-slate-800 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isUpgraded 
                  ? 'bg-green-100 dark:bg-green-900/30' 
                  : 'bg-amber-100 dark:bg-amber-900/30'
              }`}>
                {isUpgraded ? (
                  <PartyPopper className="w-10 h-10 text-green-600 dark:text-green-400" />
                ) : (
                  <Mail className="w-10 h-10 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              
              <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                {isUpgraded ? 'Welcome to Pro!' : 'Payment Successful!'}
              </CardTitle>
              
              <CardDescription className="text-base text-slate-600 dark:text-slate-400 mt-2">
                {isUpgraded 
                  ? 'Your account has been upgraded to Pro!'
                  : 'Log in or sign up to activate your Pro features.'
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {isUpgraded ? (
                <>
                  {/* Success - Pro Benefits */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                      Your Pro Benefits
                    </h4>
                    <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                      <li>✓ Unlimited verifications</li>
                      <li>✓ Priority AI analysis</li>
                      <li>✓ Image & video analysis</li>
                      <li>✓ Permanent history</li>
                      <li>✓ Deep Dive Fact-Checker search</li>
                    </ul>
                  </div>
                  
                  <Button
                    onClick={() => navigate('/')}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-6 text-lg"
                  >
                    Start Verifying Content
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  {/* Pending - Need to Login */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                      Activate Your Pro Account
                    </h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Log in or create an account using the <strong>same email</strong> you used for payment. Your Pro features will activate automatically!
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => navigate('/?login=true')}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg"
                  >
                    Log In / Sign Up
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              )}
              
              {/* Support Note */}
              <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                Questions? Contact support at{' '}
                <a href="mailto:support@kaicheckit.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@kaicheckit.com
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UpgradeSuccessPage;
